import { useCallback, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import {
  ApiError,
  createClient,
  deleteClient,
  listClients,
  startXeroConnect,
  updateClient,
} from "../lib/api";
import type { Client, ClientRemoval, NewClient } from "../lib/api";
import { isAdmin } from "../lib/auth";
import { GenerateWorkpaper } from "../components/GenerateWorkpaper";
import { PencilIcon, TrashIcon } from "../components/Icons";

const BLANK: NewClient = {
  name: "",
  abn: "",
  entity_type: "Company",
  gst_basis: "Accrual",
  frequency: "Quarterly",
};

const ENTITY_TYPES = ["Company", "Trust", "Partnership", "Individual", "SMSF"];
const GST_BASES = ["Accrual", "Cash"];
const FREQUENCIES = ["Quarterly", "Monthly", "Annual"];

interface XeroOutcome {
  kind: "connected" | "error";
  detail: string;
}

function readXeroOutcome(): XeroOutcome | null {
  const params = new URLSearchParams(window.location.search);
  const xero = params.get("xero");
  if (!xero) return null;
  return xero === "connected"
    ? { kind: "connected", detail: params.get("tenant") ?? "" }
    : { kind: "error", detail: params.get("reason") ?? "unknown" };
}

/** Outcome of the Xero redirect, handed back in the query string. */
function useXeroOutcome(): XeroOutcome | null {
  // Read once as the initial state rather than setting state from an effect:
  // the query string cannot change without a navigation, and setting state in
  // an effect would render twice for no reason.
  const [outcome] = useState(readXeroOutcome);

  useEffect(() => {
    if (!outcome) return;
    // Clear the params so a refresh does not re-show a stale banner. This
    // touches browser history, not React state.
    window.history.replaceState({}, document.title, window.location.pathname);
  }, [outcome]);

  return outcome;
}

const XERO_ERRORS: Record<string, string> = {
  invalid_state: "That connection link had expired. Start again.",
  access_denied: "Consent was declined in Xero.",
  ambiguous_tenant:
    "More than one Xero organisation is connected to Casa without being linked to a client, so this one could not be told apart. Connect one at a time, or remove the extras from Xero's Connected Apps.",
  tenants_all_claimed:
    "That Xero organisation is already connected to another Casa client. One organisation belongs to one client — disconnect it from the other client first.",
  no_tenant: "That grant covered no Xero organisation. Try again and choose one.",
  not_configured: "Xero is not configured yet. An administrator needs to set the app credentials.",
  exchange_failed: "Xero rejected the authorization. Try again.",
};

/** What became of the Xero connection when a client was deleted. */
const XERO_REMOVAL: Record<string, string> = {
  revoked: "Casa's access to their Xero organisation was revoked.",
  already_disconnected: "Casa had already been disconnected in Xero.",
  not_connected: "No Xero organisation was connected.",
  unreachable:
    "Casa could not sign in to Xero to revoke its access, because the connection had already expired. If the organisation still lists Casa under Connected Apps, it needs removing there.",
};

/** Which row, if any, has an inline form open. */
type RowMode = { kind: "edit" | "delete"; clientId: string } | null;

export function ClientsPage() {
  const auth = useAuth();
  const admin = isAdmin(auth.user);
  const outcome = useXeroOutcome();

  const [clients, setClients] = useState<Client[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [mode, setMode] = useState<RowMode>(null);
  const [removed, setRemoved] = useState<ClientRemoval | null>(null);

  const token = auth.user?.access_token;

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      // State is set only after the await, so calling this from an effect does
      // not trigger a synchronous cascading render.
      const next = await listClients(token);
      setClients(next);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load clients");
    }
  }, [token]);

  useEffect(() => {
    // Fetching on mount is synchronising with an external system, which is
    // what effects are for. The rule cannot see that refresh() only sets state
    // after its await, so there is no synchronous cascading render here.
    // oxlint-disable-next-line react/set-state-in-effect
    void refresh();
  }, [refresh]);

  async function connect(client: Client, includePayroll: boolean) {
    if (!token) return;
    setBusy(client.client_id);
    try {
      const url = await startXeroConnect(token, client.client_id, includePayroll);
      // Full navigation, not a popup: Xero's consent screen refuses to frame.
      window.location.assign(url);
    } catch (err) {
      setBusy(null);
      setError(err instanceof Error ? err.message : "Could not start the Xero connection");
    }
  }

  function toggle(kind: "edit" | "delete", clientId: string) {
    setError(null);
    setMode((current) =>
      current?.kind === kind && current.clientId === clientId ? null : { kind, clientId },
    );
  }

  async function finish(summary?: ClientRemoval) {
    setMode(null);
    if (summary) setRemoved(summary);
    await refresh();
  }

  return (
    <section>
      {outcome?.kind === "connected" && (
        <div className="notice success">
          Connected{outcome.detail ? ` to ${outcome.detail}` : ""}.
        </div>
      )}
      {outcome?.kind === "error" && (
        <div className="notice error">
          {XERO_ERRORS[outcome.detail] ?? `Could not connect Xero (${outcome.detail}).`}
        </div>
      )}
      {removed && (
        <div className="notice success">
          <strong>{removed.name} was deleted.</strong>
          <p>
            {XERO_REMOVAL[removed.xero] ?? `Xero: ${removed.xero}.`}
            {removed.workpapers > 0 &&
              ` ${removed.workpapers} workpaper(s) were removed; earlier versions stay recoverable in storage.`}
          </p>
          <button className="link" onClick={() => setRemoved(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div className="section-head">
        <h2>Clients</h2>
        {admin && <AddClient onCreated={refresh} />}
      </div>

      {error && <div className="notice error">{error}</div>}

      {clients === null ? (
        <p className="muted">Loading…</p>
      ) : clients.length === 0 ? (
        <p className="muted">
          No clients yet.{admin ? " Add one to get started." : " An administrator needs to add one."}
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Client</th>
              <th>ABN</th>
              <th>Basis</th>
              <th>Cycle</th>
              <th>Xero</th>
              <th>Workpaper</th>
              {admin && <th />}
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <ClientRow
                key={client.client_id}
                client={client}
                admin={admin}
                busy={busy === client.client_id}
                mode={mode?.clientId === client.client_id ? mode.kind : null}
                onConnect={connect}
                onToggle={toggle}
                onDone={finish}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function ClientRow(props: {
  client: Client;
  admin: boolean;
  busy: boolean;
  mode: "edit" | "delete" | null;
  onConnect: (client: Client, includePayroll: boolean) => Promise<void>;
  onToggle: (kind: "edit" | "delete", clientId: string) => void;
  onDone: (summary?: ClientRemoval) => Promise<void>;
}) {
  const { client, admin, busy, mode } = props;
  const columns = admin ? 7 : 6;

  return (
    <>
      <tr>
        <td>
          <strong>{client.name}</strong>
          <span className="muted"> · {client.entity_type}</span>
        </td>
        <td>{client.abn || <span className="muted">—</span>}</td>
        <td>{client.gst_basis}</td>
        <td>{client.frequency}</td>
        <td>
          {client.xero.connected ? (
            <>
              <span className="tag ok">Connected</span>
              {client.xero.tenant_name && (
                <span className="muted"> {client.xero.tenant_name}</span>
              )}
              {client.xero.has_payroll && <span className="tag">Payroll</span>}
            </>
          ) : (
            <span className="tag">Not connected</span>
          )}
        </td>
        <td>
          <GenerateWorkpaper client={client} admin={admin} />
        </td>
        {admin && (
          <td className="actions">
            <button disabled={busy} onClick={() => void props.onConnect(client, false)}>
              {client.xero.connected ? "Reconnect" : "Connect Xero"}
            </button>
            <button
              className="link"
              disabled={busy}
              onClick={() => void props.onConnect(client, true)}
              title="Also request Xero Payroll scopes, for W1/W2"
            >
              with Payroll
            </button>
            <button
              className="icon"
              aria-label={`Edit ${client.name}`}
              aria-expanded={mode === "edit"}
              title="Edit this client"
              onClick={() => props.onToggle("edit", client.client_id)}
            >
              <PencilIcon />
            </button>
            <button
              className="icon danger"
              aria-label={`Delete ${client.name}`}
              aria-expanded={mode === "delete"}
              title="Delete this client and disconnect Xero"
              onClick={() => props.onToggle("delete", client.client_id)}
            >
              <TrashIcon />
            </button>
          </td>
        )}
      </tr>

      {admin && mode === "edit" && (
        <tr className="drawer">
          <td colSpan={columns}>
            <EditClient client={client} onDone={props.onDone} />
          </td>
        </tr>
      )}
      {admin && mode === "delete" && (
        <tr className="drawer">
          <td colSpan={columns}>
            <DeleteClient client={client} onDone={props.onDone} />
          </td>
        </tr>
      )}
    </>
  );
}

/** The client fields, shared by adding and editing so the two cannot drift. */
function ClientFields(props: { draft: NewClient; onChange: (draft: NewClient) => void }) {
  const { draft, onChange } = props;
  return (
    <>
      <input
        aria-label="Client name"
        placeholder="Client / entity name"
        required
        value={draft.name}
        onChange={(changeEvent) => onChange({ ...draft, name: changeEvent.target.value })}
      />
      <input
        aria-label="ABN"
        placeholder="ABN"
        value={draft.abn}
        onChange={(changeEvent) => onChange({ ...draft, abn: changeEvent.target.value })}
      />
      <select
        aria-label="Entity type"
        value={draft.entity_type}
        onChange={(changeEvent) => onChange({ ...draft, entity_type: changeEvent.target.value })}
      >
        {ENTITY_TYPES.map((value) => (
          <option key={value}>{value}</option>
        ))}
      </select>
      <select
        aria-label="GST basis"
        value={draft.gst_basis}
        onChange={(changeEvent) => onChange({ ...draft, gst_basis: changeEvent.target.value })}
      >
        {GST_BASES.map((value) => (
          <option key={value}>{value}</option>
        ))}
      </select>
      <select
        aria-label="Lodgment cycle"
        value={draft.frequency}
        onChange={(changeEvent) => onChange({ ...draft, frequency: changeEvent.target.value })}
      >
        {FREQUENCIES.map((value) => (
          <option key={value}>{value}</option>
        ))}
      </select>
    </>
  );
}

function AddClient({ onCreated }: { onCreated: () => Promise<void> }) {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<NewClient>(BLANK);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(submitEvent: React.FormEvent) {
    submitEvent.preventDefault();
    const token = auth.user?.access_token;
    if (!token) return;

    setSaving(true);
    try {
      await createClient(token, draft);
      setDraft(BLANK);
      setOpen(false);
      setError(null);
      await onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the client record");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button className="primary" onClick={() => setOpen(true)}>
        Add client
      </button>
    );
  }

  return (
    <form className="add-client" onSubmit={(formEvent) => void submit(formEvent)}>
      <ClientFields draft={draft} onChange={setDraft} />
      <button className="primary" type="submit" disabled={saving}>
        {saving ? "Saving…" : "Save"}
      </button>
      <button type="button" className="link" onClick={() => setOpen(false)}>
        Cancel
      </button>
      {error && <span className="field-error">{error}</span>}
    </form>
  );
}

function EditClient(props: { client: Client; onDone: () => Promise<void> }) {
  const auth = useAuth();
  const { client } = props;
  const [draft, setDraft] = useState<NewClient>({
    name: client.name,
    abn: client.abn,
    entity_type: client.entity_type,
    gst_basis: client.gst_basis,
    frequency: client.frequency,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(submitEvent: React.FormEvent) {
    submitEvent.preventDefault();
    const token = auth.user?.access_token;
    if (!token) return;

    setSaving(true);
    try {
      await updateClient(token, client.client_id, draft);
      setError(null);
      await props.onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the changes");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="add-client" onSubmit={(formEvent) => void submit(formEvent)}>
      <ClientFields draft={draft} onChange={setDraft} />
      <button className="primary" type="submit" disabled={saving}>
        {saving ? "Saving…" : "Save changes"}
      </button>
      <button type="button" className="link" onClick={() => void props.onDone()}>
        Cancel
      </button>
      {draft.gst_basis !== client.gst_basis && (
        <span className="muted">
          Changing the GST basis applies from the next workpaper. Past workpapers keep the
          basis they were prepared on.
        </span>
      )}
      {error && <span className="field-error">{error}</span>}
    </form>
  );
}

function DeleteClient(props: {
  client: Client;
  onDone: (summary?: ClientRemoval) => Promise<void>;
}) {
  const auth = useAuth();
  const { client } = props;
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Typing the name, rather than a confirm dialog. This removes a client's
  // workpapers and revokes access to their books, and the icon that starts it
  // sits next to one that merely opens a form.
  const confirmed = typed.trim() === client.name.trim();

  async function submit(submitEvent: React.FormEvent) {
    submitEvent.preventDefault();
    const token = auth.user?.access_token;
    if (!token || !confirmed) return;

    setDeleting(true);
    try {
      const summary = await deleteClient(token, client.client_id);
      setError(null);
      await props.onDone(summary);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete the client");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <form className="confirm-delete" onSubmit={(formEvent) => void submit(formEvent)}>
      <p>
        <strong>Delete {client.name}?</strong> This removes the client record and every
        workpaper generated for it
        {client.xero.connected
          ? `, and revokes Casa's access to ${client.xero.tenant_name || "their Xero organisation"}. Reconnecting needs the client to authorise it again.`
          : "."}{" "}
        Workpapers stay recoverable from versioned storage, so this is not a way to destroy
        a record.
      </p>
      <label>
        <span className="muted">Type the client name to confirm: </span>
        <input
          aria-label="Confirm client name"
          placeholder={client.name}
          value={typed}
          onChange={(changeEvent) => setTyped(changeEvent.target.value)}
        />
      </label>
      <button className="danger" type="submit" disabled={!confirmed || deleting}>
        {deleting ? "Deleting…" : "Delete client"}
      </button>
      <button type="button" className="link" onClick={() => void props.onDone()}>
        Cancel
      </button>
      {error && <span className="field-error">{error}</span>}
    </form>
  );
}
